import { useEffect, useRef, useState } from 'react';
import {
  CHANNELS, DEPARTMENTS, DIRECTIONS, OTHER_CHANNELS, OUTCOMES, useEditCommunication, useFollowUp,
  useLogCommunication, useWithdrawCommunication, type Channel, type Communication, type CommunicationFacts,
  type Department, type DepartmentView, type Direction, type Feed, type Outcome, type Participant,
} from '../../api/communications.ts';
import { useEditReasons } from '../../api/facilityPage.ts';
import { field, hint, label, primary, secondary } from '../contacts/Sheet.tsx';
import { sentToSaid } from '../../tasks/taskView.ts';
import { numberEdit, numberToSend, numberWords } from '../../numberEdit.ts';

/** "YYYY-MM-DDTHH:MM" in the viewer's own zone, for a datetime-local field. */
function localInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
const blank = (s: string) => (s.trim() === '' ? null : s.trim());

/** What a form needs to know besides the entry: the client's contacts on file, and the reader's department. */
export interface FormContext extends DepartmentView {
  contacts: Feed['contacts'];
}

/**
 * Log a communication, or correct one (138, 139). The form is laid out for its kind (Arthur, 20 October): a call
 * asks who, incoming or outgoing, when, the time spent (required), a summary, the outcome and whether it is an
 * emergency; an email asks who it was from or to, everyone included, the subject, the body as pasted (stored
 * exactly, never cleaned), the time spent (required), when, sent or received, the outcome and emergency; a letter,
 * fax, meeting or video call asks what 138 asked. The department is the logger's own, shown; tiers 1-2 choose one.
 * A correction asks why (123's reasons, as a visit's does) and keeps the entry's department unless tiers 1-2 move it.
 */
export function CommunicationForm({ clientId, ctx, channel: initialChannel, editing, onDone }: {
  clientId: string | null; ctx: FormContext; channel: Channel; editing?: Communication; onDone: () => void;
}) {
  const log = useLogCommunication(clientId);
  const edit = useEditCommunication(editing?.id ?? '');
  const reasons = useEditReasons(!!editing);
  const [channel, setChannel] = useState<Channel>(editing?.channel ?? initialChannel);
  const kind: 'phone' | 'email' | 'other' = channel === 'phone' || channel === 'email' ? channel : 'other';
  const [when, setWhen] = useState(localInput(editing ? new Date(editing.occurred_at) : new Date()));
  const [minutes, setMinutes] = useState(editing?.duration_minutes?.toString() ?? '');
  const [direction, setDirection] = useState<Direction>(editing?.direction ?? (kind === 'other' && initialChannel !== 'letter' && initialChannel !== 'fax' ? 'none' : 'inbound'));
  const [contactId, setContactId] = useState<string>(editing?.contact_id ?? '');
  const [name, setName] = useState(editing?.contact_name ?? '');
  const [phone, setPhone] = useState(editing?.contact_phone ?? '');
  const [email, setEmail] = useState(editing?.contact_email ?? '');
  const [department, setDepartment] = useState<Department | ''>(editing?.department ?? '');
  const [summary, setSummary] = useState(editing?.summary ?? '');
  const [outcome, setOutcome] = useState<Outcome>(editing?.outcome ?? 'informational');
  const [emergency, setEmergency] = useState(editing?.is_emergency ?? false);
  const [subject, setSubject] = useState(editing?.subject ?? '');
  const [people, setPeople] = useState<Participant[]>(editing?.participants ?? []);
  const [body, setBody] = useState(editing?.body ?? '');
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');
  const m = editing ? edit : log;

  const timeRequired = kind !== 'other';
  // F-86: the box's text AND whether the browser could read it (unreadable text reports as ''); a blank never erases
  // recorded minutes on its own: removing them is an explicit tick, and only where they are optional (numberEdit.ts)
  const [minutesBad, setMinutesBad] = useState(false);
  const [removeMinutes, setRemoveMinutes] = useState(false);
  const storedMinutes = editing?.duration_minutes ?? null;
  const minutesRange = { min: timeRequired ? 1 : 0, max: 1440 };
  const minutesEdit = numberEdit(minutes, minutesBad, storedMinutes, minutesRange);
  const sentMinutes = numberToSend(minutesEdit, storedMinutes, removeMinutes);
  const minutesOk = sentMinutes !== undefined && (!timeRequired || (sentMinutes !== null && sentMinutes >= 1));
  const peopleOk = people.every((p) => p.address.trim() !== '');
  const saysWhat = kind === 'email' ? summary.trim() !== '' || subject.trim() !== '' || body.trim() !== '' : summary.trim() !== '';
  const deptOk = !ctx.picks_department || department !== '';
  const needsText = reasons.data?.reasons.find((r) => r.code === reason)?.needs_text ?? false;
  const ready = minutesOk && peopleOk && saysWhat && deptOk && (contactId || name.trim() !== '')
    && (!editing || (reason && (!needsText || reasonText.trim())));

  const save = () => {
    const facts: CommunicationFacts = {
      occurred_at: new Date(when).toISOString(), duration_minutes: sentMinutes === undefined ? storedMinutes : sentMinutes,
      channel, direction, contact_id: contactId || null,
      contact_name: contactId ? null : blank(name),
      contact_phone: contactId || kind === 'email' ? null : blank(phone),
      contact_email: contactId || kind === 'phone' ? null : blank(email),
      // tiers 1-2 choose; anyone else's is their own (new) or the entry's (a correction), which the database fills in
      department: ctx.picks_department ? (department || null) : null,
      summary: blank(summary), outcome, is_emergency: emergency,
      subject: kind === 'email' ? blank(subject) : null,
      participants: kind === 'email' ? people.map((p) => ({ kind: p.kind, name: blank(p.name ?? ''), address: p.address.trim() })) : [],
      // the body exactly as pasted: never trimmed or cleaned
      body: kind === 'email' && body !== '' ? body : null,
    };
    if (editing) edit.mutate({ ...facts, reason, reason_text: blank(reasonText) }, { onSuccess: onDone });
    else log.mutate(facts, { onSuccess: onDone });
  };

  const title = editing ? 'Correct this communication'
    : kind === 'phone' ? 'Log a phone call' : kind === 'email' ? 'Log an email'
    : ({ letter: 'Log a letter', fax: 'Log a fax', in_person: 'Log a meeting in person', video: 'Log a video call' } as Record<string, string>)[channel] ?? 'Log a communication';
  const departmentShown = editing && !ctx.picks_department ? editing.department : ctx.own_department;

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (ready) save(); }}
          className="mt-2 grid gap-3 rounded-lg border border-neutral-300 bg-panel p-3 sm:grid-cols-2">
      <h3 className="text-base font-semibold text-neutral-900 sm:col-span-2">{title}</h3>

      {kind === 'other' && (
        <label className={label}>How
          <select value={channel} onChange={(e) => setChannel(e.target.value as Channel)} className={field}>
            {(editing ? CHANNELS.filter(([k]) => k !== 'phone' && k !== 'email') : OTHER_CHANNELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
      )}

      <Who kind={kind} contacts={ctx.contacts} contactId={contactId} setContactId={setContactId}
           name={name} setName={setName} phone={phone} setPhone={setPhone} email={email} setEmail={setEmail} />

      {kind === 'phone' && (
        <Choice legend="The call was" value={direction} onChange={setDirection} options={[['inbound', 'Incoming'], ['outbound', 'Outgoing']]} />
      )}
      {kind === 'email' && (
        <Choice legend="The email was" value={direction} onChange={setDirection} options={[['inbound', 'Received'], ['outbound', 'Sent']]} />
      )}
      {kind === 'other' && (
        <label className={label}>Direction
          <select value={direction} onChange={(e) => setDirection(e.target.value as Direction)} className={field}>
            {DIRECTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
      )}

      <label className={label}>Date and time
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} max={localInput(new Date())} required className={field} />
      </label>
      <label className={label}>Time spent, in minutes{timeRequired ? '' : ' (optional)'}
        <input type="number" min={minutesRange.min} max={minutesRange.max} step={1} value={minutes}
               onChange={(e) => { setMinutesBad(e.target.validity.badInput); setRemoveMinutes(false); setMinutes(e.target.value); }}
               required={timeRequired} aria-invalid={!minutesOk} className={field} />
        {timeRequired && minutes.trim() === '' && !minutesBad && <span className={`${hint} block`}>Required on a call or an email.</span>}
        {minutesEdit.kind === 'invalid' && <span className={`${hint} block`}>Minutes must be {numberWords(minutesRange)}.</span>}
      </label>
      {!timeRequired && minutesEdit.kind === 'clear' && (
        <label className="flex min-h-[44px] items-center gap-2 text-sm text-neutral-800" htmlFor="cf-minutes-remove">
          <input id="cf-minutes-remove" type="checkbox" className="size-5 rounded border-neutral-400"
                 checked={removeMinutes} onChange={(e) => setRemoveMinutes(e.target.checked)} />
          Remove the recorded {storedMinutes} minutes from this entry
        </label>
      )}

      {kind === 'email' && (
        <>
          <Participants people={people} setPeople={setPeople} />
          <label className={`${label} sm:col-span-2`}>Subject
            <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={1000} autoComplete="off" className={field} />
          </label>
          <label className={`${label} sm:col-span-2`}>Body
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} spellCheck={false}
                      className={`${field} py-2 font-mono text-sm`} />
            <span className={`${hint} block`}>Paste the email. It is kept exactly as pasted.</span>
          </label>
        </>
      )}

      {kind !== 'email' ? (
        <label className={`${label} sm:col-span-2`}>Summary
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} className={`${field} py-2`} required />
        </label>
      ) : editing?.summary ? (
        <label className={`${label} sm:col-span-2`}>Summary (optional)
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={2} className={`${field} py-2`} />
        </label>
      ) : null}

      {ctx.picks_department ? (
        <label className={label}>Department
          <select value={department} onChange={(e) => setDepartment(e.target.value as Department | '')} className={field} required>
            <option value="">Choose a department</option>
            {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
      ) : (
        <p className={label}>Department
          <span className="mt-1 block min-h-[44px] rounded-lg bg-tint px-3 py-2.5 font-semibold text-brand-text">
            {departmentShown ?? 'None of your roles names one'}
          </span>
        </p>
      )}
      <label className={label}>Outcome
        <select value={outcome} onChange={(e) => setOutcome(e.target.value as Outcome)} className={field}>
          {OUTCOMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      <label className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-neutral-800 sm:col-span-2">
        <input type="checkbox" checked={emergency} onChange={(e) => setEmergency(e.target.checked)} /> This is an emergency
      </label>

      {editing && (
        <>
          <label className={label}>Why is it being corrected?
            <select value={reason} onChange={(e) => setReason(e.target.value)} className={field} required>
              <option value="">Choose a reason</option>
              {(reasons.data?.reasons ?? []).map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </label>
          <label className={label}>In words{needsText ? '' : ' (optional)'}
            <input value={reasonText} onChange={(e) => setReasonText(e.target.value)} className={field} />
          </label>
          <p className={`${hint} sm:col-span-2`}>The version it was is kept, with who changed it, when and why.</p>
        </>
      )}
      {m.error && <p role="alert" className="text-sm text-warning-fg sm:col-span-2">{(m.error as Error).message}</p>}
      <div className="flex flex-wrap gap-2 sm:col-span-2">
        <button type="submit" disabled={!ready || m.isPending} className={primary}>{m.isPending ? 'Saving…' : editing ? 'Save the correction' : 'Log it'}</button>
        <button type="button" onClick={onDone} className={secondary}>Cancel</button>
      </div>
    </form>
  );
}

/**
 * Who it was with: a search over the client's contacts on file, or a name with a number (a call), an address (an
 * email), or either (anything else). The search runs in the browser over the list the feed already holds.
 */
function Who({ kind, contacts, contactId, setContactId, name, setName, phone, setPhone, email, setEmail }: {
  kind: 'phone' | 'email' | 'other'; contacts: Feed['contacts']; contactId: string; setContactId: (v: string) => void;
  name: string; setName: (v: string) => void; phone: string; setPhone: (v: string) => void; email: string; setEmail: (v: string) => void;
}) {
  const [q, setQ] = useState('');
  const picked = contacts.find((c) => c.id === contactId);
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = words.length === 0 ? contacts.slice(0, 8)
    : contacts.filter((c) => words.every((w) => `${c.name} ${c.role ?? ''} ${c.phone ?? ''} ${c.email ?? ''}`.toLowerCase().includes(w))).slice(0, 8);
  const legend = kind === 'email' ? 'From, or emailing with' : 'Who';
  return (
    <fieldset className="space-y-2 sm:col-span-2">
      <legend className={label}>{legend}</legend>
      {picked ? (
        <p className="flex flex-wrap items-center gap-2">
          <span className="rounded-lg bg-tint px-3 py-2 font-semibold text-brand-text">
            {picked.name}{picked.role ? `, ${picked.role}` : ''}{kind === 'email' && picked.email ? ` <${picked.email}>` : kind === 'phone' && picked.phone ? `, ${picked.phone}` : ''}
          </span>
          <button type="button" className={secondary} onClick={() => setContactId('')}>Someone else</button>
        </p>
      ) : (
        <>
          {contacts.length > 0 && (
            <>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this client's contacts"
                     aria-label="Search this client's contacts" autoComplete="off" className={field} />
              {matches.length > 0 ? (
                <ul className="flex flex-wrap gap-2">
                  {matches.map((c) => (
                    <li key={c.id}>
                      <button type="button" className={secondary} onClick={() => setContactId(c.id)}>
                        {c.name}{c.role ? `, ${c.role}` : ''}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <p className={hint}>No contact on file matches. Type their details below.</p>}
            </>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            <label className={label}>Name
              <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" className={field} />
            </label>
            {kind !== 'email' && (
              <label className={label}>Phone{kind === 'phone' ? '' : ' (optional)'}
                <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="off" className={field} />
              </label>
            )}
            {kind !== 'phone' && (
              <label className={label}>Email address{kind === 'email' ? '' : ' (optional)'}
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" className={field} />
              </label>
            )}
          </div>
        </>
      )}
    </fieldset>
  );
}

/** Everyone the email included, as a list: To, CC or BCC, an optional name, and the address. */
function Participants({ people, setPeople }: { people: Participant[]; setPeople: (p: Participant[]) => void }) {
  const set = (i: number, patch: Partial<Participant>) => setPeople(people.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <fieldset className="space-y-2 sm:col-span-2">
      <legend className={label}>Everyone included (CC, BCC)</legend>
      {people.length === 0 && <p className={hint}>Nobody else. Add each person copied.</p>}
      {people.map((p, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-[7rem_1fr_1fr_auto]">
          <select aria-label="To, CC or BCC" value={p.kind} onChange={(e) => set(i, { kind: e.target.value as Participant['kind'] })} className={field}>
            <option value="cc">CC</option><option value="bcc">BCC</option><option value="to">To</option>
          </select>
          <input aria-label="Name (optional)" placeholder="Name (optional)" value={p.name ?? ''} onChange={(e) => set(i, { name: e.target.value })}
                 autoComplete="off" className={field} />
          <input aria-label="Email address" placeholder="Email address" type="email" value={p.address} onChange={(e) => set(i, { address: e.target.value })}
                 autoComplete="off" required className={field} />
          <button type="button" className={secondary} onClick={() => setPeople(people.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <button type="button" className={secondary} onClick={() => setPeople([...people, { kind: 'cc', name: '', address: '' }])}>+ Add a person</button>
    </fieldset>
  );
}

function Choice<T extends string>({ legend, value, onChange, options }: {
  legend: string; value: T; onChange: (v: T) => void; options: [T, string][];
}) {
  return (
    <fieldset className="sm:col-span-2">
      <legend className={label}>{legend}</legend>
      <div className="mt-1 flex flex-wrap gap-2" role="radiogroup">
        {options.map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)}
                  className={`min-h-[44px] rounded-lg border px-4 font-semibold ${value === k ? 'border-brand bg-brand text-on-brand' : 'border-neutral-400 text-neutral-800'}`}>
            {l}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/** The big logging buttons' one look: brand fill, large type, 56px tall (above the 44px floor). Colours are tokens. */
const bigButton = 'min-h-[56px] rounded-lg bg-brand px-6 text-lg font-semibold text-on-brand';

/**
 * The logging buttons (Arthur, 20 October, 139): "Log phone call" and "Log email" as two big buttons, and "Other..."
 * in the same style, opening a stack of matching buttons for a letter, a fax, a meeting in person or a video call
 * (Arthur, 16:17: the native dropdown it replaced read white on white in the dark theme). Each opens the form already
 * set to that kind.
 */
export function LogButtons({ onPick }: { onPick: (c: Channel) => void }) {
  return (
    <div className="flex flex-wrap items-start gap-2">
      <button type="button" onClick={() => onPick('phone')} className={bigButton}>Log phone call</button>
      <button type="button" onClick={() => onPick('email')} className={bigButton}>Log email</button>
      <OtherMenu onPick={onPick} />
    </div>
  );
}

/**
 * "Other...": opens on hover, on click or tap (a tablet has no hover), and from the keyboard (Tab onto it, Enter,
 * Space or the down arrow); closes when the pointer leaves, on Escape (focus returns to it), on a click elsewhere, or
 * when focus leaves it. The arrow keys, Home, End and Tab move through the stack.
 */
function OtherMenu({ onPick }: { onPick: (c: Channel) => void }) {
  const [open, setOpen] = useState(false);
  // Fixed below the trigger, measured when it opens: the section panel clips anything absolutely placed past its edge
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  // Escape hands focus back to the trigger; that focus must not open it again
  const quiet = useRef(false);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = trigger.current?.getBoundingClientRect();
      if (r) setAt({ top: r.bottom, left: r.left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const away = (e: PointerEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', away);
    return () => {
      document.removeEventListener('pointerdown', away);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const focusItem = (i: number) => {
    const n = OTHER_CHANNELS.length;
    items.current[((i % n) + n) % n]?.focus();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    const at = items.current.findIndex((el) => el === document.activeElement);
    if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); quiet.current = true; trigger.current?.focus(); quiet.current = false; return; }
    // open already: focus at once; opening: once the stack is drawn
    const go = (i: number) => { if (open) focusItem(i); else { setOpen(true); setTimeout(() => focusItem(i), 0); } };
    if (e.key === 'ArrowDown') { e.preventDefault(); go(at + 1); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); go(at < 0 ? -1 : at - 1); return; }
    if (e.key === 'Home' && at >= 0) { e.preventDefault(); focusItem(0); return; }
    if (e.key === 'End' && at >= 0) { e.preventDefault(); focusItem(-1); }
  };

  return (
    <div ref={wrap} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
         onKeyDown={onKeyDown}
         onBlur={(e) => { if (!wrap.current?.contains(e.relatedTarget as Node | null)) setOpen(false); }}>
      <button ref={trigger} type="button" aria-haspopup="true" aria-expanded={open} className={bigButton}
              onFocus={() => { if (!quiet.current) setOpen(true); }} onClick={() => setOpen(true)}>
        Other…
      </button>
      {open && at && (
        // pt-2 rather than a margin: the padding belongs to the menu, so the pointer moving onto the stack never leaves it
        <div className="fixed z-30 pt-2" style={{ top: at.top, left: at.left }}>
          <ul aria-label="Log something else" className="flex min-w-[12rem] flex-col gap-2 rounded-lg border border-neutral-300 bg-panel p-2 shadow-lg">
            {OTHER_CHANNELS.map(([k, l], i) => (
              <li key={k}>
                <button ref={(el) => { items.current[i] = el; }} type="button" className={`${bigButton} w-full text-left`}
                        onClick={() => { setOpen(false); onPick(k); }}>
                  {l}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function WithdrawForm({ id, onDone }: { id: string; onDone: () => void }) {
  const w = useWithdrawCommunication(id);
  const [reason, setReason] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (reason.trim()) w.mutate(reason.trim(), { onSuccess: onDone }); }}
          className="mt-2 space-y-2 rounded-lg border border-neutral-300 bg-panel p-3">
      <label className={label}>Why is it being withdrawn?
        <input value={reason} onChange={(e) => setReason(e.target.value)} className={field} required />
      </label>
      <p className={hint}>It stays on record, marked, and counts toward nothing.</p>
      {w.error && <p role="alert" className="text-sm text-warning-fg">{(w.error as Error).message}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={!reason.trim() || w.isPending} className={primary}>Withdraw it</button>
        <button type="button" onClick={onDone} className={secondary}>Cancel</button>
      </div>
    </form>
  );
}

/**
 * The follow-up task in one click (Q6): pre-filled with the client's primary case manager in the entry's department,
 * else whoever is logged in's choice; a due date is required. Once made, it says who it was sent to (A-#154).
 */
export function FollowUpForm({ entry, feed, onDone }: { entry: Communication; feed: Feed; onDone: (said?: string) => void }) {
  const f = useFollowUp(entry.id);
  const primaryId = feed.primary.find((p) => p.department === entry.department)?.id ?? '';
  const [title, setTitle] = useState(`Follow up: ${(entry.summary ?? entry.subject ?? '').split('\n')[0]!.slice(0, 120)}`);
  const [to, setTo] = useState(primaryId);
  const tomorrow = new Date(Date.now() + 86400_000);
  const [due, setDue] = useState(localInput(tomorrow).slice(0, 10));
  return (
    <form onSubmit={(e) => { e.preventDefault(); f.mutate({ title, detail: null, assigned_to: to, due_date: due }, { onSuccess: (r) => onDone(sentToSaid(r.sent_to)) }); }}
          className="mt-2 grid gap-2 rounded-lg border border-neutral-300 bg-panel p-3 sm:grid-cols-2">
      <label className={`${label} sm:col-span-2`}>Task
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className={field} required />
      </label>
      <label className={label}>For
        <select value={to} onChange={(e) => setTo(e.target.value)} className={field} required>
          <option value="">Choose</option>
          {feed.staff.map((s) => <option key={s.id} value={s.id}>{s.full_name}{s.id === primaryId ? ' (primary case manager)' : ''}</option>)}
        </select>
      </label>
      <label className={label}>Due
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={field} required />
      </label>
      <p className={`${hint} sm:col-span-2`}>A {entry.department} task for this client{entry.is_emergency ? ', marked High because the entry is an emergency' : ''}.</p>
      {f.error && <p role="alert" className="text-sm text-warning-fg sm:col-span-2">{(f.error as Error).message}</p>}
      <div className="flex flex-wrap gap-2 sm:col-span-2">
        <button type="submit" disabled={!to || !title.trim() || f.isPending} className={primary}>Make the task</button>
        <button type="button" onClick={() => onDone()} className={secondary}>Cancel</button>
      </div>
    </form>
  );
}
